(() => {
const APPROVED_FUEL_VALUES = Object.freeze({
  natural_gas: Object.freeze(['Natural Gas']),
  propane: Object.freeze(['Propane', 'Liquid Propane'])
});
const TIGHT_FIT_BUFFER_INCHES = 1;

const CATEGORY_LABELS = Object.freeze({
  manual_safety_pilot: 'Manual Safety Pilot',
  standing_pilot_controls: 'Standing Pilot Controls',
  electronic_ignition: 'Electronic Ignition'
});

// Exact, approved Shopify ignition values only. Products with a different
// value are excluded until that value is deliberately mapped here.
const CONTROL_DEFINITIONS = Object.freeze({
  'Manual Safety Pilot (Match-free Start)': Object.freeze({
    category: 'manual_safety_pilot', key: 'manual_no_remote', label: 'Manual flame control — no remote',
    description: 'Use the built-in controls to turn the burner on or off and adjust the flame.'
  }),
  'Manual Safety Pilot (Manual Flame Control)': Object.freeze({
    category: 'manual_safety_pilot', key: 'manual_no_remote', label: 'Manual flame control — no remote',
    description: 'Use the built-in control knob to operate and adjust the burner.'
  }),
  'Manual Safety Pilot (Variable Flame On/Off Knob)': Object.freeze({
    category: 'manual_safety_pilot', key: 'manual_no_remote', label: 'Manual flame control — no remote',
    description: 'Use the built-in control knob to operate and adjust the burner.'
  }),
  'Toggle Switch (Standing Pilot - Remote Ready)': Object.freeze({
    category: 'standing_pilot_controls', key: 'toggle_remote_ready', label: 'Toggle switch — remote-ready',
    description: 'Operate with the built-in toggle switch, with the option to add a compatible remote later.'
  }),
  'On/Off Remote (Standing Pilot)': Object.freeze({
    category: 'standing_pilot_controls', key: 'on_off_remote', label: 'On/off remote',
    description: 'Use a handheld remote to turn the burner on or off.'
  }),
  'Thermostat Remote (Standing Pilot)': Object.freeze({
    category: 'standing_pilot_controls', key: 'thermostat_remote', label: 'Thermostat remote',
    description: 'Use a handheld thermostat remote to cycle the burner and help maintain the selected room temperature.'
  }),
  'Variable Flame Remote (Standing Pilot)': Object.freeze({
    category: 'standing_pilot_controls', key: 'variable_flame_remote', label: 'Variable-flame remote',
    description: 'Use a handheld remote to raise or lower the flame.'
  }),
  'G18 - See-Thru Vent Free Variable Remote Burner': Object.freeze({
    category: 'standing_pilot_controls', key: 'variable_flame_remote', label: 'Variable-flame remote',
    description: 'Use the supplied remote to operate the burner and adjust flame height.'
  }),
  'Thermostat + Variable Flame Remote (Electronic Ignition)': Object.freeze({
    category: 'electronic_ignition', key: 'thermostat_variable_remote', label: 'Thermostat + variable-flame remote',
    description: 'Use the handheld remote for thermostat operation and flame-height adjustment.'
  })
});

class AgwGasLogFinder {
  constructor(root) {
    this.root = root;
    this.steps = Array.from(root.querySelectorAll('[data-agw-finder-step]'));
    this.form = root.querySelector('[data-agw-finder-form]');
    this.results = root.querySelector('[data-agw-finder-results]');
    this.resultsHeading = root.querySelector('[data-agw-finder-results-heading]');
    this.resultsIntro = root.querySelector('[data-agw-finder-results-intro]');
    this.resultGrid = root.querySelector('[data-agw-finder-result-grid]');
    this.validation = root.querySelector('[data-agw-finder-validation]');
    this.backButton = root.querySelector('[data-agw-finder-back]');
    this.nextButton = root.querySelector('[data-agw-finder-next]');
    this.progressBar = root.querySelector('[data-agw-finder-progress-bar]');
    this.progressText = root.querySelector('[data-agw-finder-progress-text]');
    this.progress = root.querySelector('[data-agw-finder-progress]');
    this.consultation = root.querySelector('.agw-finder__consultation');
    this.supportPanel = root.querySelector('[data-agw-finder-support]');
    this.controlChoices = root.querySelector('[data-agw-finder-control-choices]');
    this.controlHint = root.querySelector('[data-agw-finder-control-hint]');
    this.systemHeading = root.querySelector('[data-agw-finder-system-heading]');
    this.systemHint = root.querySelector('[data-agw-finder-system-hint]');
    this.helpDisclosures = Array.from(root.querySelectorAll('.agw-finder__help'));
    this.activeHelpDisclosure = null;
    this.instanceId = root.getAttribute('aria-labelledby') || 'agw-gas-log-finder';
    this.storageKey = `agw-gas-log-finder:${window.location.pathname}${window.location.search}:${this.instanceId}`;
    this.restoringState = true;
    this.productMappings = new Map();
    this.currentStep = 0;
    this.products = this.readProducts();

    if (!this.form || !this.steps.length) return;

    root.dataset.enhanced = 'true';
    this.bindEvents();
    const savedState = this.restoreState();
    this.showStep(savedState?.currentStep ?? 0, false);
    if (savedState?.view === 'results') this.renderResults({ focus: false });
    this.restoringState = false;
  }

  readProducts() {
    return Array.from(this.root.querySelectorAll('[data-agw-finder-product]')).flatMap((node) => {
      try {
        const product = JSON.parse(node.textContent);
        const mapping = this.buildProductMapping(product);
        if (!mapping) return [];
        this.productMappings.set(product.handle, mapping);
        return [product];
      } catch (error) {
        console.warn('Gas log finder skipped invalid candidate data.', error);
        return [];
      }
    });
  }

  buildProductMapping(product) {
    if (!['single_sided', 'see_through'].includes(product.configuration)
      || !['vented', 'vent_free'].includes(product.system)
      || !['oak', 'birch', 'driftwood', 'split_oak', 'charred'].includes(product.style)) return null;

    const fuels = Object.fromEntries(Object.entries(APPROVED_FUEL_VALUES).flatMap(([key, approvedValues]) => {
      const exactValue = approvedValues.find((value) => (product.fuels || []).includes(value));
      return exactValue ? [[key, exactValue]] : [];
    }));
    if (!Object.keys(fuels).length) return null;

    const categories = {};

    Object.entries(CONTROL_DEFINITIONS).forEach(([variantValue, definition]) => {
      const hasExactMatchableConfiguration = (product.variants || []).some((variant) => {
        if (!variant.available || variant.ignition !== variantValue) return false;
        const fuelKey = Object.entries(fuels).find(([, exactValue]) => exactValue === variant.fuel)?.[0];
        if (!fuelKey) return false;
        const matchingVariants = (product.variants || []).filter((candidate) => {
          return candidate.available
            && candidate.fuel === variant.fuel
            && candidate.size === variant.size
            && candidate.ignition === variantValue;
        });
        const matchingRows = (product.sizes || []).filter((row) => {
          return row.size === variant.size
            && row.ignitionTitle === variantValue
            && this.hasCompleteCompatibilityRow(row)
            && this.rowSupportsFuel(row, fuelKey);
        });
        return matchingVariants.length === 1 && matchingRows.length === 1;
      });
      if (!hasExactMatchableConfiguration) return;
      const category = categories[definition.category] || {
        label: CATEGORY_LABELS[definition.category],
        controls: []
      };
      category.controls.push({ ...definition, variantValue });
      categories[definition.category] = category;
    });

    if (!Object.keys(categories).length) return null;
    return { fuels, categories };
  }

  bindEvents() {
    this.nextButton.addEventListener('click', () => this.continue());
    this.backButton.addEventListener('click', () => this.showStep(this.currentStep - 1));
    this.form.addEventListener('change', (event) => {
      this.closeHelpPopups();
      this.clearValidation();
      if (event.target.name === 'configuration') this.updateSystemChoices();
      if (['configuration', 'system'].includes(event.target.name)) {
        this.updateStyleChoices();
      }
      if (['configuration', 'system', 'style'].includes(event.target.name)) {
        this.updateFuelChoices();
      }
      if (['configuration', 'system', 'style', 'fuel'].includes(event.target.name)) {
        this.updateIgnitionChoices();
      }
      if (event.target.name === 'ignition') this.renderControlChoices(event.target.value);
      this.updateNextButtonState();
      this.persistState();
    });
    this.form.addEventListener('input', () => {
      this.clearValidation();
      this.updateNextButtonState();
      this.persistState();
    });
    this.root.querySelectorAll('[data-agw-finder-restart]').forEach((button) => {
      button.addEventListener('click', () => this.restart());
    });

    this.helpDisclosures.forEach((disclosure) => {
      disclosure.querySelector('summary')?.setAttribute('aria-expanded', String(disclosure.open));
    });
    this.root.addEventListener('toggle', (event) => this.handleHelpToggle(event), true);
    document.addEventListener('click', (event) => this.handleOutsideClick(event));
    document.addEventListener('keydown', (event) => this.handleHelpKeydown(event));
  }

  handleHelpToggle(event) {
    const disclosure = event.target;
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.classList.contains('agw-finder__help')) return;

    disclosure.querySelector('summary')?.setAttribute('aria-expanded', String(disclosure.open));
    if (!disclosure.open) {
      if (this.activeHelpDisclosure === disclosure) this.activeHelpDisclosure = null;
      return;
    }

    this.closeHelpPopups({ except: disclosure });
    this.activeHelpDisclosure = disclosure;
  }

  handleOutsideClick(event) {
    if (this.activeHelpDisclosure && !this.activeHelpDisclosure.contains(event.target)) this.closeHelpPopups();
  }

  handleHelpKeydown(event) {
    if (event.key !== 'Escape' || !this.activeHelpDisclosure) return;
    event.preventDefault();
    this.closeHelpPopups({ restoreFocus: true });
  }

  closeHelpPopups({ except = null, restoreFocus = false } = {}) {
    const disclosureToRefocus = restoreFocus ? this.activeHelpDisclosure : null;
    this.helpDisclosures.forEach((disclosure) => {
      if (disclosure === except || !disclosure.open) return;
      disclosure.open = false;
      disclosure.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
    this.activeHelpDisclosure = except?.open ? except : null;
    if (disclosureToRefocus && disclosureToRefocus !== except) disclosureToRefocus.querySelector('summary')?.focus();
  }

  showStep(index, focus = true) {
    this.closeHelpPopups();
    delete this.root.dataset.view;
    this.currentStep = Math.max(0, Math.min(index, this.steps.length - 1));
    this.results.dataset.active = 'false';
    this.steps.forEach((step, stepIndex) => {
      step.dataset.active = String(stepIndex === this.currentStep);
    });
    const currentStep = this.steps[this.currentStep];
    if (currentStep.querySelector('[name="system"]')) this.updateSystemChoices();
    if (currentStep.querySelector('[name="style"]')) this.updateStyleChoices();
    if (currentStep.querySelector('[name="fuel"]')) this.updateFuelChoices();
    if (currentStep.querySelector('[name="ignition"]')) this.updateIgnitionChoices();
    if (currentStep.querySelector('[data-agw-finder-control-choices]')) {
      const selectedIgnition = this.form.querySelector('[name="ignition"]:checked');
      this.renderControlChoices(selectedIgnition?.value || null);
    }
    this.backButton.hidden = this.currentStep === 0;
    this.nextButton.textContent = this.currentStep === this.steps.length - 1 ? 'See my recommendations' : 'Continue';
    this.updateNextButtonState();
    this.updateProgress();
    this.clearValidation();

    if (focus) this.focusCurrentHeading();
    this.persistState();
  }

  persistState() {
    if (this.restoringState) return;
    try {
      const answers = Object.fromEntries(new FormData(this.form).entries());
      window.sessionStorage.setItem(this.storageKey, JSON.stringify({
        answers,
        currentStep: this.currentStep,
        view: this.root.dataset.view === 'results' ? 'results' : 'steps',
        savedAt: Date.now()
      }));
    } catch (error) {
      console.warn('Gas log finder could not save progress for this browser tab.', error);
    }
  }

  restoreState() {
    try {
      const rawState = window.sessionStorage.getItem(this.storageKey);
      if (!rawState) return null;
      const state = JSON.parse(rawState);
      const isExpired = !state.savedAt || Date.now() - state.savedAt > 8 * 60 * 60 * 1000;
      if (isExpired || !state.answers || typeof state.answers !== 'object') {
        this.clearSavedState();
        return null;
      }

      Object.entries(state.answers).forEach(([name, value]) => {
        if (name === 'control') return;
        this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach((input) => {
          if (input.type === 'radio' || input.type === 'checkbox') {
            input.checked = input.value === value;
          } else {
            input.value = value;
          }
        });
      });

      const selectedIgnition = this.form.querySelector('[name="ignition"]:checked');
      this.renderControlChoices(selectedIgnition?.value || null, state.answers.control || null);
      return state;
    } catch (error) {
      this.clearSavedState();
      console.warn('Gas log finder could not restore saved progress.', error);
      return null;
    }
  }

  clearSavedState() {
    try {
      window.sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Gas log finder could not clear saved progress.', error);
    }
  }

  updateProgress() {
    const displayedStep = this.currentStep + 1;
    const percent = (displayedStep / this.steps.length) * 100;
    this.progressText.textContent = `Step ${displayedStep} of ${this.steps.length}`;
    this.progressBar.style.width = `${percent}%`;
    this.progress.setAttribute('aria-valuenow', String(displayedStep));
  }

  focusCurrentHeading() {
    const heading = this.steps[this.currentStep].querySelector('[data-agw-finder-heading]');
    if (!heading) return;
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
    this.consultation.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  continue() {
    this.closeHelpPopups();
    if (!this.validateCurrentStep()) return;

    if (this.currentStep < this.steps.length - 1) {
      this.showStep(this.currentStep + 1);
      return;
    }

    this.renderResults();
  }

  validateCurrentStep() {
    const step = this.steps[this.currentStep];
    const requiredInputs = Array.from(step.querySelectorAll('[required]:not(:disabled)'));
    const radioNames = [...new Set(requiredInputs.filter((input) => input.type === 'radio').map((input) => input.name))];
    const missingRadio = radioNames.find((name) => !this.form.querySelector(`[name="${CSS.escape(name)}"]:checked`));
    const missingInput = requiredInputs.find((input) => {
      if (input.type === 'radio') return false;
      if (!input.value || !input.checkValidity()) return true;
      if (input.matches('[data-agw-finder-measurement]')) {
        const measurement = Number(input.value);
        return !Number.isFinite(measurement) || measurement <= 0;
      }
      return false;
    });

    if (!missingRadio && !missingInput) return true;

    this.validation.textContent = missingRadio
      ? 'Choose an option before continuing.'
      : 'Enter all four usable interior measurements using numbers greater than zero.';

    const target = missingInput || step.querySelector(`[name="${CSS.escape(missingRadio)}"]`);
    if (target) target.focus();
    return false;
  }

  isStepComplete(step) {
    const requiredInputs = Array.from(step.querySelectorAll('[required]:not(:disabled)'));
    const radioNames = [...new Set(requiredInputs.filter((input) => input.type === 'radio').map((input) => input.name))];
    if (radioNames.some((name) => !step.querySelector(`[name="${CSS.escape(name)}"]:checked`))) return false;
    return requiredInputs.filter((input) => input.type !== 'radio').every((input) => {
      if (!input.value || !input.checkValidity()) return false;
      if (!input.matches('[data-agw-finder-measurement]')) return true;
      const measurement = Number(input.value);
      return Number.isFinite(measurement) && measurement > 0;
    });
  }

  updateNextButtonState() {
    const currentStep = this.steps[this.currentStep];
    if (!currentStep || !this.nextButton || this.root.dataset.view === 'results') return;
    this.nextButton.disabled = !this.isStepComplete(currentStep);
  }

  clearValidation() {
    if (this.validation) this.validation.textContent = '';
  }

  getAnswers() {
    const data = new FormData(this.form);
    return {
      configuration: data.get('configuration'),
      system: data.get('system'),
      style: data.get('style'),
      fuel: data.get('fuel'),
      frontWidth: Number(data.get('front_width')),
      rearWidth: Number(data.get('rear_width')),
      height: Number(data.get('height')),
      depth: Number(data.get('depth')),
      ignition: data.get('ignition'),
      control: data.get('control')
    };
  }

  matchProducts(answers) {
    return this.products.flatMap((product) => {
      if (!this.productMatchesBaseAnswers(product, answers)) return [];

      const exactControl = this.getExactControl(product, answers);
      if (!exactControl) return [];

      const compatibleSizes = this.getVerifiedCompatibleSizes(product, answers, exactControl);
      if (!compatibleSizes.length) return [];

      compatibleSizes.sort((a, b) => this.numberFrom(b.row.size) - this.numberFrom(a.row.size));
      const recommended = compatibleSizes.find((size) => !size.isTightFit) || compatibleSizes[0];
      return [{
        product,
        compatibleSizes,
        recommended,
        largestCompatible: compatibleSizes[0],
        recommendationAdjusted: recommended !== compatibleSizes[0],
        exactControl
      }];
    });
  }

  getVerifiedCompatibleSizes(product, answers, exactControl) {
    const mapping = this.getProductMapping(product);
    const exactFuelValue = mapping?.fuels?.[answers.fuel];
    if (!exactFuelValue) return [];

    const variantsBySize = new Map();
    (product.variants || []).forEach((variant) => {
      if (!variant.available || variant.fuel !== exactFuelValue || variant.ignition !== exactControl.variantValue) return;
      if (!Number.isFinite(this.numberFrom(variant.size))) return;
      const variants = variantsBySize.get(variant.size) || [];
      variants.push(variant);
      variantsBySize.set(variant.size, variants);
    });

    return Array.from(variantsBySize.entries()).flatMap(([size, variants]) => {
      // A size is verifiable only when there is one unambiguous available
      // variant and one unambiguous structured requirement row for the exact
      // fuel, size, and approved ignition value.
      if (variants.length !== 1) return [];
      const rows = (product.sizes || []).filter((row) => {
        return row.size === size && row.ignitionTitle === exactControl.variantValue;
      });
      if (rows.length !== 1 || !this.rowSupportsFuel(rows[0], answers.fuel) || !this.hasCompleteCompatibilityRow(rows[0])) return [];
      return this.rowFits(rows[0], answers)
        ? [this.addFitGuidance({ row: rows[0], variant: variants[0] }, answers)]
        : [];
    });
  }

  addFitGuidance(sizeMatch, answers) {
    const requirements = {
      frontWidth: this.numberFrom(sizeMatch.row.frontWidth),
      rearWidth: this.numberFrom(sizeMatch.row.rearWidth),
      height: this.numberFrom(sizeMatch.row.height),
      depth: this.numberFrom(sizeMatch.row.depth)
    };
    const clearances = Object.fromEntries(Object.entries(requirements).map(([dimension, requirement]) => {
      return [dimension, answers[dimension] - requirement];
    }));
    const tightDimensions = Object.entries(clearances)
      .filter(([, clearance]) => clearance >= 0 && clearance <= TIGHT_FIT_BUFFER_INCHES)
      .map(([dimension, clearance]) => ({ dimension, clearance }));
    return {
      ...sizeMatch,
      clearances,
      tightDimensions,
      isTightFit: tightDimensions.length > 0
    };
  }

  supportsFuel(product, fuel) {
    const exactFuelValue = this.getProductMapping(product)?.fuels?.[fuel];
    return Boolean(exactFuelValue && (product.fuels || []).includes(exactFuelValue));
  }

  productMatchesBaseAnswers(product, answers) {
    if (product.configuration !== answers.configuration || product.system !== answers.system) return false;
    if (answers.style !== 'no_preference' && product.style !== answers.style) return false;
    return this.supportsFuel(product, answers.fuel);
  }

  getEligibleProducts({ includeSystem = true, includeStyle = true, includeFuel = true } = {}) {
    const answers = this.getAnswers();
    return this.products.filter((product) => {
      if (answers.configuration && product.configuration !== answers.configuration) return false;
      if (includeSystem && answers.system && product.system !== answers.system) return false;
      if (includeStyle && answers.style && answers.style !== 'no_preference' && product.style !== answers.style) return false;
      if (includeFuel && answers.fuel && !this.supportsFuel(product, answers.fuel)) return false;
      return Boolean(this.getProductMapping(product));
    });
  }

  updateSystemChoices() {
    const products = this.getEligibleProducts({ includeSystem: false, includeStyle: false, includeFuel: false });
    const availableSystems = new Set(products.map((product) => product.system));
    this.updateDependentChoices('[data-agw-finder-system-choice]', 'system', availableSystems);

    const configuration = this.form.querySelector('[name="configuration"]:checked')?.value;
    const onlyVentedSeeThrough = configuration === 'see_through'
      && availableSystems.size === 1
      && availableSystems.has('vented');
    if (this.systemHeading) {
      this.systemHeading.textContent = onlyVentedSeeThrough
        ? 'Vented options for your see-through or multi-sided fireplace'
        : 'Does your fireplace require vented or vent-free gas logs?';
    }
    if (this.systemHint) {
      this.systemHint.textContent = onlyVentedSeeThrough
        ? 'Our current verified options for this fireplace type are vented. Vent-free options are not yet available in this finder.'
        : 'Choose the type of gas log system your fireplace is designed to use.';
    }
  }

  updateStyleChoices() {
    const products = this.getEligibleProducts({ includeStyle: false, includeFuel: false });
    const availableStyles = new Set(products.map((product) => product.style));
    this.root.querySelectorAll('[data-agw-finder-style-choice]').forEach((choice) => {
      const input = choice.querySelector('[name="style"]');
      const available = input.value === 'no_preference' ? products.length > 0 : availableStyles.has(input.value);
      choice.hidden = !available;
      input.disabled = !available;
      input.required = available;
      if (!available) input.checked = false;
      this.renderStyleMedia(choice, input.value, products);
    });
  }

  renderStyleMedia(choice, style, products) {
    const media = choice.querySelector('[data-agw-finder-style-media]');
    if (!media) return;
    media.replaceChildren();
    media.classList.remove('agw-finder__style-media--collage');

    const imageProducts = style === 'no_preference'
      ? Array.from(new Map(products.filter((product) => product.image).map((product) => [product.style, product])).values()).slice(0, 4)
      : products.filter((product) => product.style === style && product.image).slice(0, 1);

    imageProducts.forEach((product) => {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    });
    if (imageProducts.length > 1) media.classList.add('agw-finder__style-media--collage');
    media.hidden = imageProducts.length === 0;
  }

  updateFuelChoices() {
    const products = this.getEligibleProducts({ includeFuel: false });
    const availableFuels = new Set(Object.keys(APPROVED_FUEL_VALUES).filter((fuel) => {
      return products.some((product) => this.supportsFuel(product, fuel));
    }));
    this.updateDependentChoices('[data-agw-finder-fuel-choice]', 'fuel', availableFuels);
  }

  updateDependentChoices(selector, inputName, availableValues) {
    this.root.querySelectorAll(selector).forEach((choice) => {
      const input = choice.querySelector(`[name="${inputName}"]`);
      if (!input) return;
      const available = availableValues.has(input.value);
      choice.hidden = !available;
      input.disabled = !available;
      input.required = available;
      if (!available) input.checked = false;
    });
  }

  updateIgnitionChoices() {
    const products = this.getEligibleProducts();
    const availableCategories = new Set(products.flatMap((product) => {
      return Object.keys(this.getProductMapping(product)?.categories || {});
    }));
    let selectionCleared = false;
    this.root.querySelectorAll('[data-agw-finder-ignition-choice]').forEach((choice) => {
      const input = choice.querySelector('[name="ignition"]');
      const available = availableCategories.has(input.value);
      choice.hidden = !available;
      input.disabled = !available;
      input.required = available;
      if (!available && input.checked) {
        input.checked = false;
        selectionCleared = true;
      }
    });
    if (selectionCleared) this.renderControlChoices(null);
  }

  rowSupportsFuel(row, fuel) {
    return fuel === 'natural_gas' ? Boolean(row.naturalGasData) : Boolean(row.propaneData);
  }

  rowFits(row, answers) {
    const frontWidth = this.numberFrom(row.frontWidth);
    const rearWidth = this.numberFrom(row.rearWidth);
    const height = this.numberFrom(row.height);
    const depth = this.numberFrom(row.depth);
    if (![frontWidth, rearWidth, height, depth].every((value) => Number.isFinite(value) && value > 0)) return false;
    return answers.frontWidth >= frontWidth
      && answers.rearWidth >= rearWidth
      && answers.height >= height
      && answers.depth >= depth;
  }

  hasCompleteCompatibilityRow(row) {
    return [row.frontWidth, row.rearWidth, row.height, row.depth]
      .map((value) => this.numberFrom(value))
      .every((value) => Number.isFinite(value) && value > 0);
  }

  getProductMapping(product) {
    return this.productMappings.get(product.handle) || null;
  }

  getExactControl(product, answers) {
    const category = this.getProductMapping(product)?.categories?.[answers.ignition];
    return category?.controls?.find((control) => control.key === answers.control) || null;
  }

  renderResults({ focus = true } = {}) {
    this.closeHelpPopups();
    const answers = this.getAnswers();
    const matches = this.matchProducts(answers);
    this.resultGrid.replaceChildren();

    if (!matches.length) {
      if (this.resultsHeading) this.resultsHeading.textContent = 'We need a closer look';
      if (this.resultsIntro) this.resultsIntro.hidden = true;
      if (this.supportPanel) this.supportPanel.hidden = true;
      const empty = document.createElement('div');
      empty.className = 'agw-finder__empty';
      empty.innerHTML = '<strong>We can’t confidently verify a compatible size for this combination.</strong><br>Please use the Fireplace Compatibility Review to send us a photo and your four measurements. An American Gas Works specialist will help review the setup.';
      this.resultGrid.append(empty);
    } else {
      if (this.resultsHeading) this.resultsHeading.textContent = 'Your compatible options';
      if (this.resultsIntro) this.resultsIntro.hidden = false;
      if (this.supportPanel) this.supportPanel.hidden = false;
      matches.forEach((match) => this.resultGrid.append(this.buildProductCard(match, answers)));
    }

    this.steps.forEach((step) => { step.dataset.active = 'false'; });
    this.root.dataset.view = 'results';
    this.results.dataset.active = 'true';
    this.backButton.hidden = true;
    this.nextButton.hidden = true;
    this.root.querySelector('[data-agw-finder-progress]').hidden = true;
    this.persistState();
    if (focus) {
      this.root.querySelector('[data-agw-finder-results-heading]').focus({ preventScroll: true });
      this.consultation.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  buildProductCard(match, answers) {
    const { product, recommended, compatibleSizes } = match;
    const { variant } = recommended;
    const article = document.createElement('article');
    article.className = 'agw-finder__product';

    const media = document.createElement('div');
    media.className = 'agw-finder__product-image-wrap';
    if (product.image) {
      const image = document.createElement('img');
      image.className = 'agw-finder__product-image';
      image.src = product.image;
      image.alt = product.imageAlt || product.title;
      image.loading = 'lazy';
      media.append(image);
    }

    const content = document.createElement('div');
    content.className = 'agw-finder__product-content';

    const matchLabel = document.createElement('p');
    matchLabel.className = 'agw-finder__match-label';
    matchLabel.textContent = 'Recommended match';

    const title = document.createElement('h3');
    title.className = 'agw-finder__product-title';
    title.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'agw-finder__product-price';
    const priceLabel = document.createElement('span');
    priceLabel.textContent = 'Recommended Configuration Price';
    const priceValue = document.createElement('strong');
    priceValue.textContent = variant?.price || product.price;
    price.append(priceLabel, priceValue);

    const sizeSummary = this.buildSizeSummary(match);
    const tightFitAdvisory = this.buildTightFitAdvisory(match);

    const reasons = document.createElement('ul');
    reasons.className = 'agw-finder__match-list';
    this.matchReasons(match, answers).forEach((reason) => {
      const item = document.createElement('li');
      item.textContent = reason;
      reasons.append(item);
    });

    const reasonsHeading = document.createElement('h4');
    reasonsHeading.className = 'agw-finder__reasons-heading ff-heading';
    reasonsHeading.textContent = 'Why this is a great match';

    const confirmation = document.createElement('p');
    confirmation.className = 'agw-finder__confirmation-note';
    confirmation.textContent = 'Before ordering, confirm final fit and model-specific operation in the product owner’s manual.';

    const link = document.createElement('a');
    link.className = 'agw-finder__button agw-finder__button--primary';
    link.href = variant?.url || product.url;
    link.textContent = 'View Product';

    content.append(matchLabel, title, sizeSummary);
    if (tightFitAdvisory) content.append(tightFitAdvisory);
    content.append(price, reasonsHeading, reasons, confirmation, link);
    article.append(media, content);
    return article;
  }

  buildSizeSummary(match) {
    const { compatibleSizes, recommended } = match;
    const recommendedSize = this.numberFrom(recommended.row.size);
    const tightFitAlternatives = compatibleSizes.filter((size) => (
      size !== recommended
      && size.isTightFit
      && this.numberFrom(size.row.size) > recommendedSize
    ));
    const summary = document.createElement('div');
    summary.className = 'agw-finder__size-summary';

    const recommendedLabel = document.createElement('span');
    recommendedLabel.className = 'agw-finder__size-label';
    recommendedLabel.textContent = 'Recommended Size';
    const recommendedValue = document.createElement('strong');
    recommendedValue.className = 'agw-finder__recommended-size';
    recommendedValue.textContent = recommended.row.size;
    summary.append(recommendedLabel, recommendedValue);

    if (tightFitAlternatives.length) {
      const alsoLabel = document.createElement('span');
      alsoLabel.className = 'agw-finder__size-label agw-finder__size-label--also';
      alsoLabel.textContent = 'Tight-fit option';
      const alsoList = document.createElement('ul');
      alsoList.className = 'agw-finder__also-compatible';
      tightFitAlternatives.forEach(({ row }) => {
        const item = document.createElement('li');
        item.textContent = row.size;
        alsoList.append(item);
      });
      summary.append(alsoLabel, alsoList);
    }
    return summary;
  }

  buildTightFitAdvisory(match) {
    const fit = match.recommendationAdjusted ? match.largestCompatible : match.recommended;
    if (!fit?.isTightFit) return null;

    const advisory = document.createElement('div');
    advisory.className = 'agw-finder__tight-fit';
    const heading = document.createElement('strong');
    heading.textContent = 'American Gas Works fit guidance';
    const copy = document.createElement('p');
    copy.textContent = match.recommendationAdjusted
      ? `Your measurements are very close to the minimum clearance for the ${fit.row.size} log set. While it should fit based on the information provided, we recommend the ${match.recommended.row.size} set for more installation flexibility. We’d still be happy to review your fireplace before you order.`
      : `Your measurements are very close to the minimum clearance for the ${fit.row.size} log set. While it should fit based on the information provided, we’d like to review your fireplace before recommending this size with confidence.`;
    advisory.append(heading, copy);
    return advisory;
  }

  matchReasons(match, answers) {
    const { product, exactControl } = match;
    const configuration = answers.configuration === 'see_through' ? 'See-through' : 'Single-sided';
    const system = answers.system === 'vented' ? 'vented' : 'vent-free';
    const fuel = answers.fuel === 'natural_gas' ? 'natural gas' : 'propane';
    const mapping = product && this.getProductMapping(product);
    const category = mapping?.categories?.[answers.ignition];
    const reasons = [
      `Designed for a ${configuration.toLowerCase()}, ${system} fireplace`,
      'Fits the fireplace dimensions you entered',
      `Supports ${fuel}`,
      `Works with ${category?.label || 'the selected ignition'} and ${exactControl?.label || 'control'}`,
      `Matches your ${this.styleLabel(product.style).toLowerCase()} log style`
    ];
    if (match.recommendationAdjusted) {
      reasons.push('Provides more installation flexibility than the larger tight-fit option');
    } else if (match.recommended.isTightFit) {
      reasons.push('Qualifies from the measurements provided; a professional review is recommended');
    } else {
      reasons.push('Provides more than 1″ of room beyond every minimum clearance');
    }
    return reasons;
  }

  renderControlChoices(categoryKey, selectedControlKey = this.form.querySelector('[name="control"]:checked')?.value || null) {
    if (!this.controlChoices) return;
    this.controlChoices.replaceChildren();

    const category = this.getSharedCategory(categoryKey);
    if (!category) {
      if (this.controlHint) this.controlHint.textContent = 'Choose an ignition category first.';
      return;
    }

    if (this.controlHint) this.controlHint.textContent = category.controlHint;

    category.controls.forEach((control) => {
      const choice = document.createElement('div');
      choice.className = 'agw-finder__choice';

      const input = document.createElement('input');
      input.id = `${this.instanceId}-${control.key}`;
      input.type = 'radio';
      input.name = 'control';
      input.value = control.key;
      input.required = true;
      input.checked = control.key === selectedControlKey;

      const label = document.createElement('label');
      label.htmlFor = input.id;

      const title = document.createElement('span');
      title.className = 'agw-finder__choice-title';
      title.textContent = control.label;

      const copy = document.createElement('span');
      copy.className = 'agw-finder__choice-copy';
      copy.textContent = control.description;

      label.append(title, copy);
      choice.append(input, label);
      this.controlChoices.append(choice);
    });
  }

  getSharedCategory(categoryKey) {
    const categories = this.getEligibleProducts()
      .map((product) => this.getProductMapping(product)?.categories?.[categoryKey])
      .filter(Boolean);
    if (!categories.length) return null;

    const controls = new Map();
    categories.forEach((category) => {
      category.controls.forEach((control) => {
        if (!controls.has(control.key)) controls.set(control.key, control);
      });
    });
    return {
      label: categories[0].label,
      controlHint: controls.size === 1
        ? 'One verified control choice is available for this combination.'
        : 'Choose from the verified controls available for this combination.',
      controls: Array.from(controls.values())
    };
  }

  restart() {
    this.closeHelpPopups();
    this.clearSavedState();
    this.form.reset();
    this.renderControlChoices(null);
    this.resultGrid.replaceChildren();
    if (this.resultsHeading) this.resultsHeading.textContent = 'Your compatible options';
    if (this.resultsIntro) this.resultsIntro.hidden = false;
    if (this.supportPanel) this.supportPanel.hidden = false;
    this.nextButton.hidden = false;
    this.root.querySelector('[data-agw-finder-progress]').hidden = false;
    this.showStep(0);
  }

  numberFrom(value) {
    if (typeof value === 'number') return value;
    const match = String(value || '').match(/[\d.]+/);
    return match ? Number(match[0]) : Number.NaN;
  }

  capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  styleLabel(style) {
    return {
      oak: 'Oak',
      birch: 'Birch',
      driftwood: 'Driftwood',
      split_oak: 'Split Oak',
      charred: 'Charred'
    }[style] || 'Selected';
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

document.querySelectorAll('[data-agw-gas-log-finder]').forEach((root) => new AgwGasLogFinder(root));
})();
